import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import { EyeIcon, EyeSlashIcon, LockClosedIcon, UserIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const { login, loading: authLoading } = useAuth();
  const { getLogoUrl, getLogoDarkUrl } = useCompanyLogo();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});

  // A dónde volver tras iniciar sesión (?returnTo=/activo/xxx). Solo rutas internas.
  const returnToRaw = new URLSearchParams(window.location.search).get('returnTo') || '';
  const returnTo = returnToRaw.startsWith('/') && !returnToRaw.startsWith('//') ? returnToRaw : '/dashboard';

  const handleLogin = async () => {
    // Validación simple
    if (!username || !password) {
      setErrors({
        username: !username ? 'Usuario requerido' : undefined,
        password: !password ? 'Contraseña requerida' : undefined
      });
      toast.error('Por favor completa todos los campos');
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await axios.post('/api/auth/login', {
        username: username,
        password: password
      });

      if (response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        toast.success('¡Bienvenido!');

        // Redirigir a la página de origen (o al dashboard)
        setTimeout(() => {
          window.location.href = returnTo;
        }, 300);
      } else {
        toast.error('Error: Respuesta inválida del servidor');
      }
    } catch (error: any) {
      // Si hay un error de 2FA, manejarlo aquí
      if (error.response?.data?.requires2FA) {
        setRequires2FA(true);
        toast.success('Se requiere verificación de dos factores');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error('Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async () => {
    try {
      setTwoFALoading(true);
      
      const response = await axios.post('/api/2fa/verify-login', {
        username,
        token: useBackupCode ? undefined : twoFactorToken,
        backupCode: useBackupCode ? backupCode : undefined
      });
      
      if (response.data.token) {
        // Store token and redirect
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        window.location.href = returnTo;
      }
    } catch (error: any) {
      console.error('2FA verification error:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Error en la verificación de dos factores');
      }
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTwoFactorToken('');
    setBackupCode('');
    setUseBackupCode(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      {/* Switch de cambio de tema estilo iOS */}
      <div className="absolute top-6 right-6">
        <Switch
          id="theme-switch"
          checked={isDarkMode}
          onCheckedChange={toggleTheme}
          aria-label="Cambiar tema"
          className="data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:bg-gray-600 data-[state=checked]:bg-green-500 h-6 w-11 border-0"
        />
      </div>

      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center mb-8">
          <img
            src={isDarkMode ? getLogoDarkUrl() : getLogoUrl()}
            onError={(e:any)=>{e.currentTarget.src='/rocam-new-logo-2025.png';}}
            alt="Logo"
            className="h-20 w-auto object-contain"
          />
        </div>

        <div>
          {!requires2FA ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="Ingresa tu usuario"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={`pl-9 ${errors.username ? 'border-destructive' : ''}`}
                    autoComplete="username"
                  />
                </div>
                {errors.username && (
                  <p className="text-sm text-destructive">{errors.username}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ingresa tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleLogin();
                      }
                    }}
                    className={`pl-9 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              <Button
                onClick={handleLogin}
                disabled={loading}
                className="w-full border-2 border-primary"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Iniciando sesión...
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="h-4 w-4 mr-2" />
                    Iniciar Sesión
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-muted border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Verificación de dos factores
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {useBackupCode
                        ? 'Ingresa uno de tus códigos de respaldo'
                        : 'Ingresa el código de 6 dígitos de tu aplicación de autenticación'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="2fa-token">
                  {useBackupCode ? 'Código de respaldo' : 'Código de verificación'}
                </Label>
                <Input
                  id="2fa-token"
                  type="text"
                  placeholder={useBackupCode ? 'ABCDEF' : '000000'}
                  value={useBackupCode ? backupCode : twoFactorToken}
                  onChange={(e) => useBackupCode ? setBackupCode(e.target.value) : setTwoFactorToken(e.target.value)}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setUseBackupCode(!useBackupCode)}
                  className="p-0 h-auto"
                >
                  {useBackupCode ? 'Usar código de verificación' : 'Usar código de respaldo'}
                </Button>

                <Button
                  type="button"
                  variant="link"
                  onClick={handleBackToLogin}
                  className="p-0 h-auto"
                >
                  Volver
                </Button>
              </div>

              <Button
                onClick={handle2FAVerification}
                disabled={twoFALoading || (!useBackupCode && twoFactorToken.length !== 6) || (useBackupCode && backupCode.length !== 6)}
                className="w-full border-2 border-primary"
              >
                {twoFALoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verificando...
                  </>
                ) : (
                  <>
                    <ShieldCheckIcon className="h-4 w-4 mr-2" />
                    Verificar
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login; 